import { Section } from '../tools/section';
import { CLASS_ACTIVE, CLASS_VISIBLE, CLASS_HIDDEN, CLASS_LOADED } from '../tools/constants/classes';

const SEL_HERO = '[data-animated-hero]'
const SEL_HERO_SLIDE_CONTROL = '[data-animated-hero-control]'
const SEL_HERO_SLIDES = '[data-animated-hero-slide]'
const SEL_HERO_SCATTERED_PIECE = '[data-scattered-piece]';
const SEL_HERO_UNSCATTERED_PIECE = '[data-unscattered-piece]';
const SEL_HERO_MOVING_PIECE = '[data-moving-piece]';
const SEL_HERO_TO_REVEAL = '[data-to-reveal-1]';
const SEL_HERO_TO_REVEAL_2 = '[data-to-reveal-2]';
const SEL_HERO_TO_HIDE = '[data-to-hide]';
const SEL_HERO_PRETITLE = '[data-pretitle]';
const SEL_HERO_SUBTITLE = '[data-subtitle]';
const SEL_HERO_ARROW = '[data-scroll-arrow]';
const SEL_HERO_CONTROL_WRAPPER = '[data-hero-controls]';

const ATTR_HERO_CONTROL = 'data-animated-hero-control'
const ATTR_HERO_ACTIVE_SLIDE = 'data-active-slide'
const ATTR_HERO_AUTOPLAY = 'data-autoplay'
const ATTR_HERO_AUTOPLAY_SPEED = 'data-autoplay-speed'

export const firstRevealDuration = 1800;
export const secondRevealDuration = 4000;
export const totalRevealDuration = firstRevealDuration + secondRevealDuration;

let scatteredPositionsArray: { top: number; left: number; height: number }[] = [];
let unscatteredPositionsArray: { top: number; left: number; height: number }[] = [];

class AnimatedHeroBanner extends Section {
  private slideControls: NodeListOf<HTMLButtonElement>|null = null;
  private slides: NodeListOf<HTMLDivElement>|null = null;
  private scatteredPieces: NodeListOf<HTMLDivElement>|null = null;
  private unscatteredPieces: NodeListOf<HTMLDivElement>|null = null;
  private movingPieces: NodeListOf<HTMLDivElement>|null = null;
  private elementsToReveal: NodeListOf<HTMLElement>|null = null;
  private elementsToReveal2: NodeListOf<HTMLElement>|null = null;
  private elementsToHide: NodeListOf<HTMLElement>|null = null;
  private pretitle: HTMLElement|null = null;
  private subtitle: HTMLElement|null = null;
  private scrollArrow: NodeListOf<HTMLElement>|null = null;
  private controlWrapper: HTMLElement|null = null;
  private intervalId: NodeJS.Timeout | null = null;
  
  queryChildren(container:HTMLElement) {
    this.slides = container.querySelectorAll(SEL_HERO_SLIDES);
    this.slideControls = container.querySelectorAll(SEL_HERO_SLIDE_CONTROL);
    this.scatteredPieces = container.querySelectorAll(SEL_HERO_SCATTERED_PIECE);
    this.unscatteredPieces = container.querySelectorAll(SEL_HERO_UNSCATTERED_PIECE);
    this.movingPieces = container.querySelectorAll(SEL_HERO_MOVING_PIECE);
    this.elementsToReveal = container.querySelectorAll(SEL_HERO_TO_REVEAL);
    this.elementsToReveal2 = container.querySelectorAll(SEL_HERO_TO_REVEAL_2);
    this.elementsToHide = container.querySelectorAll(SEL_HERO_TO_HIDE);
    this.pretitle = container.querySelector(SEL_HERO_PRETITLE);
    this.subtitle = container.querySelector(SEL_HERO_SUBTITLE);
    this.scrollArrow = container.querySelectorAll(SEL_HERO_ARROW);
    this.controlWrapper = container.querySelector(SEL_HERO_CONTROL_WRAPPER);
  }
  
  attachEvents() {
    this.slideControls?.forEach(btn => {
      btn.addEventListener('click', this.handleSlideControlClick.bind(this));
    });

    this.scrollArrow?.forEach(btn => {
      btn.addEventListener('click', this.scrollDown.bind(this));
    });

    this.startAnimation();
  }

  detatchEvents() {
    this.slideControls?.forEach(btn => {
      btn.removeEventListener('click', this.handleSlideControlClick);
    });
  }

  scrollDown() {
    const currentSectionHeight = this.container.offsetHeight;

    window.scrollTo({
      top: currentSectionHeight,
      behavior: 'smooth'
    });
  }

  startAnimation() {
    const isAutoplay = this.container.getAttribute(ATTR_HERO_AUTOPLAY) || "false";
    const autoplaySpeed = this.container.getAttribute(ATTR_HERO_AUTOPLAY_SPEED) || "3";
    const autoplaySpeedSeconds = parseInt(autoplaySpeed) * 1000;

    this.getScatteredTitlePositions();
    this.getUnscatteredTitlePositions();
    this.setMovingTitlePositions(scatteredPositionsArray);

    setTimeout(() => {
      this.setMovingTitlePositions(unscatteredPositionsArray);
      this.adjustSubtitlePosition();
      this.revealElements1();
    }, firstRevealDuration);

    setTimeout(() => {
      this.revealElements2();
    
      // Get updated position of the basis elements
      this.getUnscatteredTitlePositions();
      this.setMovingTitlePositions(unscatteredPositionsArray);
      this.adjustPretitlePosition();
      this.hideElements();

      // Autoplay
      if (isAutoplay == "true" && this.slides && this.slides.length > 1) {
        this.intervalId = setInterval(this.startAutoPlay.bind(this),autoplaySpeedSeconds);
      }
    }, secondRevealDuration);

    setTimeout(() => {
      this.container.classList.add(CLASS_LOADED);
    }, (secondRevealDuration * 1.5));
  }

  startAutoPlay() {
    if (!this.slides) return;
    
    const currentIndex = Array.from(this.slides).findIndex(slide =>
      slide.classList.contains('is-active')
    );

    if (currentIndex !== -1) {
      this.slides[currentIndex].classList.remove('is-active');
    }

    // Calculate the next index (check if we're at the last slide)
    let nextIndex = currentIndex + 1;
    if (nextIndex >= this.slides.length) {
      nextIndex = 0;  // Reset to the first slide if we're at the end
    }

    this.activateSlide(nextIndex);
  }

  adjustPretitlePosition() {
    if (!this.pretitle || unscatteredPositionsArray[0] == null) return;
    const height = this.pretitle.offsetHeight;
    const top = unscatteredPositionsArray[0].top - height;
    this.pretitle.style.top = `${top}px`;
  }

  adjustSubtitlePosition() {
    if (!this.subtitle) return;

    const top = unscatteredPositionsArray[0].top + unscatteredPositionsArray[0].height;
    this.subtitle.style.top = `${top}px`;
  }

  handleSlideControlClick(e:MouseEvent) {
    const self = e.target as HTMLElement;
    const activeSlideControl = self.getAttribute(ATTR_HERO_CONTROL);

    if (!activeSlideControl) return;
    this.activateSlide(activeSlideControl);

    // Stop autoplay when the user interacts with the controls
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  activateSlide(slideIndex) {
    this.slides?.forEach((slide,index) => {
      const indexString = index.toString();
      if (indexString == slideIndex) {
        slide.classList.add(CLASS_ACTIVE);
      } else {
        slide.classList.remove(CLASS_ACTIVE);
      }
    });

    this.slideControls?.forEach((btn,index) => {
      const indexString = index.toString();
      if (indexString == slideIndex) {
        btn.classList.add(CLASS_ACTIVE);
      } else {
        btn.classList.remove(CLASS_ACTIVE);
      }
    });

    this.controlWrapper?.setAttribute(ATTR_HERO_ACTIVE_SLIDE,slideIndex);
  }

  hideElements() {
    this.elementsToHide?.forEach(element => {
      element.classList.add(CLASS_HIDDEN);
      element.classList.remove(CLASS_VISIBLE);
    });
  }

  // Reveal first batch of elements
  revealElements1() {
    this.elementsToReveal?.forEach(element => {
      element.classList.remove(CLASS_HIDDEN);
    });
  }

  // Reveal second batch of elements
  revealElements2() {
    this.elementsToReveal2?.forEach(element => {
      element.classList.remove(CLASS_HIDDEN);
    });
  }

  getScatteredTitlePositions() {
    scatteredPositionsArray = [];

    this.scatteredPieces?.forEach((piece,index) => {
      const position = {
        top: piece.offsetTop,
        left: piece.offsetLeft,
        height: piece.offsetHeight
      };

      scatteredPositionsArray.push(position);
    });  
  }

  getUnscatteredTitlePositions() {
    unscatteredPositionsArray = [];

    this.unscatteredPieces?.forEach((piece,index) => {
      const position = {
        top: piece.offsetTop,
        left: piece.offsetLeft,
        height: piece.offsetHeight
      };
      unscatteredPositionsArray.push(position);
    });  
  }

  setMovingTitlePositions(positions) {
    this.movingPieces?.forEach((piece,index) => {
      const {top,left,height} = positions[index];
      piece.style.top = `${top}px`;
      piece.style.left = `${left}px`;
      piece.classList.add(CLASS_VISIBLE);
    });

    this.scatteredPieces?.forEach(piece => {
      piece.classList.add(CLASS_HIDDEN);
    });  
  }
}

Section.register(SEL_HERO, AnimatedHeroBanner);